import { jsx as _jsx } from "react/jsx-runtime";
// Salvaged from ui-sprocket src/theme/ColorModeProvider.tsx — reviewed 2026-09-21: the
// `theme-color` meta values now come from `NEUTRAL` tokens; also sets `data-theme` on <html>
// so the emitted CSS custom properties follow the resolved mode.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NEUTRAL } from '../tokens/color.js';
import { ColorModeContext, readStoredPreference, resolveMode, writeStoredPreference, } from './color-mode.js';
const DARK_QUERY = '(prefers-color-scheme: dark)';
function systemPrefersDark() {
    return typeof window !== 'undefined' && window.matchMedia?.(DARK_QUERY).matches === true;
}
/**
 * Holds the colour-mode choice for the whole app. Sits ABOVE the tenant theme so the
 * preference survives switching tenants, and so a themed error/login screen for an
 * unresolved tenant still honours it. When the preference is `system` the OS is tracked
 * live — changing appearance in the OS while the app is open re-themes it immediately.
 */
export function ColorModeProvider({ children, initialPreference, }) {
    const [preference, setPreferenceState] = useState(() => initialPreference ?? readStoredPreference());
    const [prefersDark, setPrefersDark] = useState(() => systemPrefersDark());
    useEffect(() => {
        const query = window.matchMedia?.(DARK_QUERY);
        if (!query)
            return;
        const onChange = (event) => setPrefersDark(event.matches);
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);
    const setPreference = useCallback((next) => {
        setPreferenceState(next);
        writeStoredPreference(next);
    }, []);
    const value = useMemo(() => ({ preference, mode: resolveMode(preference, prefersDark), setPreference }), [preference, prefersDark, setPreference]);
    // Tell the browser which scheme is active so built-in UI (form controls, scrollbars,
    // the address bar on mobile) matches the app; `theme-color` is the installed-PWA
    // equivalent. `data-theme` drives the CSS custom properties for non-MUI markup.
    useEffect(() => {
        const root = document.documentElement;
        root.style.colorScheme = value.mode;
        root.dataset['theme'] = value.mode;
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta)
            meta.setAttribute('content', NEUTRAL[value.mode].canvas);
    }, [value.mode]);
    return _jsx(ColorModeContext.Provider, { value: value, children: children });
}
//# sourceMappingURL=color-mode-provider.js.map